/* Production Dashboard — action-oriented landing for a production technician.
 *
 * Mirrors PlannerDashboard's compact dense layout, but tuned for the
 * manufacturing workflow:
 *
 *   1. Header + (conditional) Priority breach banner (STAT/Rush cases
 *      sitting longer than they should)
 *   2. 6-tile compact action grid — STAT, Rush, Awaiting routing,
 *      Doctor-planned, In production, On hold
 *   3. Today's quick stats — Shipped today, In flight, Shipped this week,
 *      Avg cycle (approved → shipped in last 7 days)
 *   4. Up Next list — priority-first, then oldest. Top 6.
 *
 * The deep queue + dispatch screens live on /coe/production (queue page).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    Package, ArrowRight, Truck, ClipboardText, PlayCircle, Pause, Stack,
    Fire, ClockClockwise, CheckCircle, PaperPlaneTilt, Lightning, ShieldWarning,
    Tray, Sun, Moon, Cloud, Tooth, Target,
} from "@phosphor-icons/react";


/* Hours-since helper — duplicated from SLAHelpers since the production
   side has its own cycle thresholds (12h warn, 24h breach for awaiting). */
function hoursSince(iso) {
    if (!iso) return 0;
    return Math.max(0, (Date.now() - new Date(iso).getTime()) / 3600000);
}

function isToday(iso) {
    if (!iso) return false;
    const d = new Date(iso);
    const n = new Date();
    return d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
}

function isInLast7Days(iso) {
    if (!iso) return false;
    return hoursSince(iso) <= 24 * 7;
}

/* iter97.19 — Shift helpers for the My Shift context chip. */
function currentShift() {
    const h = new Date().getHours();
    if (h >= 6 && h < 14) return { id: "morning", label: "Morning shift", Icon: Sun, cls: "text-amber-600 bg-amber-50 border-amber-200" };
    if (h >= 14 && h < 22) return { id: "afternoon", label: "Afternoon shift", Icon: Cloud, cls: "text-cyan-700 bg-cyan-50 border-cyan-200" };
    return { id: "night", label: "Night shift", Icon: Moon, cls: "text-indigo-700 bg-indigo-50 border-indigo-200" };
}

function fmtDateShort(d) {
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/* Target turnaround for the Avg Cycle stat — used as the benchmark indicator. */
const TARGET_CYCLE_HOURS = 24;


/* Production SLA threshold — STAT > 6h with us is a breach signal.
   Rush > 18h, Standard > 36h. Used to drive the priority banner. */
const SLA_HOURS = {
    stat: 6,
    rush: 18,
    standard: 36,
};


/* Bucket a case into a tile category. Production handles ~5 statuses;
   we map each to the action the tech needs to take. */
function bucketOf(c) {
    const s = c.status;
    if (c.production_on_hold) return "on_hold";
    if (s === "in_production") return "in_production";
    if (s === "submitted_to_production") return "doctor_planned";
    if (s === "approved" || s === "assigned_to_production") return "awaiting";
    return "other";
}


/* Stage bucket — used for the second tile row. Reads
   production_stage when present, otherwise falls back to status. */
function stageBucketOf(c) {
    if (c.production_on_hold) return null;        // already covered by on_hold tile
    const stage = c.production_stage || "";
    // Stages before fabrication = "assigned" (just accepted, not yet fabricating)
    if (["assigned", "digital_prep"].includes(stage)) return "assigned_stage";
    // Active fabrication
    if (["model_production", "thermoforming", "trimming_finishing", "cleaning"].includes(stage)) return "in_fabrication";
    // QC stage
    if (stage === "qc_review") return "qc_pending";
    // Packaging = ready to ship
    if (stage === "packaging") return "ready_to_ship";
    return null;
}


/* Tile meta — priority-focused (1st row). Awaiting/Doctor-planned both
   link to the queue with the right filter so the tech can dive straight
   into work without re-filtering. */
const TILE_META = [
    { id: "stat",           label: "STAT priority",     Icon: Lightning,     tone: "rose",    to: "/coe/production/queue?priority=stat" },
    { id: "rush",           label: "Rush priority",     Icon: Fire,          tone: "amber",   to: "/coe/production/queue?priority=rush" },
    { id: "awaiting",       label: "Awaiting routing",  Icon: ClipboardText, tone: "violet",  to: "/coe/production/queue" },
    { id: "doctor_planned", label: "Doctor-planned",    Icon: PaperPlaneTilt,tone: "cyan",    to: "/coe/production/queue" },
    { id: "in_production",  label: "In production",     Icon: PlayCircle,    tone: "primary", to: "/coe/production/queue" },
    { id: "on_hold",        label: "On hold",           Icon: Pause,         tone: "rose",    to: "/coe/production/queue?tab=on_hold" },
];

/* Stage-aware tiles — 2nd row. Tells the tech where the work is along the
   10-stage SOP, not just the priority. */
const STAGE_TILE_META = [
    { id: "assigned_stage", label: "Assigned",       Icon: Tray,        tone: "violet",  to: "/coe/production/queue?stage=assigned" },
    { id: "in_fabrication", label: "In fabrication", Icon: Stack,       tone: "primary", to: "/coe/production/queue?stage=fabrication" },
    { id: "qc_pending",     label: "QC pending",     Icon: ShieldWarning,tone: "amber",  to: "/coe/production/queue?stage=qc" },
    { id: "ready_to_ship",  label: "Ready to ship",  Icon: Package,     tone: "emerald", to: "/coe/production/queue?stage=packaging" },
];

const TONES = {
    primary: { bg: "bg-primary",       text: "text-white",         soft: "bg-white/15",            icon: "text-white",          sub: "text-white/85",       border: "border-transparent" },
    amber:   { bg: "bg-amber-50",      text: "text-amber-900",     soft: "bg-amber-100",           icon: "text-amber-700",      sub: "text-amber-800/85",    border: "border-amber-200" },
    rose:    { bg: "bg-rose-50",       text: "text-rose-900",      soft: "bg-rose-100",            icon: "text-rose-700",       sub: "text-rose-800/85",     border: "border-rose-200" },
    violet:  { bg: "bg-violet-50",     text: "text-violet-900",    soft: "bg-violet-100",          icon: "text-violet-700",     sub: "text-violet-800/85",   border: "border-violet-200" },
    emerald: { bg: "bg-emerald-50",    text: "text-emerald-900",   soft: "bg-emerald-100",         icon: "text-emerald-700",    sub: "text-emerald-800/85",  border: "border-emerald-200" },
    cyan:    { bg: "bg-cyan-50",       text: "text-cyan-900",      soft: "bg-cyan-100",            icon: "text-cyan-700",       sub: "text-cyan-800/85",     border: "border-cyan-200" },
};


export default function ProductionDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [queueCases, setQueueCases] = useState([]);
    const [shippedCases, setShippedCases] = useState([]);
    const [onHoldCases, setOnHoldCases] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/production/queue").catch(() => ({ data: [] })),
            api.get("/production/shipped").catch(() => ({ data: [] })),
            api.get("/production/on-hold").catch(() => ({ data: [] })),
        ]).then(([q, s, h]) => {
            setQueueCases(q.data || []);
            setShippedCases(s.data || []);
            setOnHoldCases(h.data || []);
        }).finally(() => setLoading(false));
    }, []);

    /* Tile counts. Queue cases roll up into stat/rush/awaiting/doctor_planned/
       in_production. On-hold has its own bucket (separate endpoint). */
    const counts = useMemo(() => {
        const out = { stat: 0, rush: 0, awaiting: 0, doctor_planned: 0, in_production: 0, on_hold: onHoldCases.length };
        for (const c of queueCases) {
            const b = bucketOf(c);
            if (out[b] !== undefined) out[b] += 1;
            const p = c.production_priority || "standard";
            if (p === "stat") out.stat += 1;
            else if (p === "rush") out.rush += 1;
        }
        return out;
    }, [queueCases, onHoldCases]);

    /* Stage-aware counts for the 2nd tile row. Reads
       production_stage on each case and buckets accordingly. */
    const stageCounts = useMemo(() => {
        const out = { assigned_stage: 0, in_fabrication: 0, qc_pending: 0, ready_to_ship: 0 };
        for (const c of queueCases) {
            const b = stageBucketOf(c);
            if (b && out[b] !== undefined) out[b] += 1;
        }
        return out;
    }, [queueCases]);

    const totalAction = counts.stat + counts.rush + counts.awaiting + counts.doctor_planned + counts.on_hold;

    /* Cases that have breached their priority-specific SLA. Shown in the
       hero banner so the tech sees the fires immediately. */
    const slaBreaches = useMemo(() => {
        return queueCases.filter((c) => {
            if (c.status !== "approved" && c.status !== "assigned_to_production" && c.status !== "submitted_to_production") return false;
            const p = c.production_priority || "standard";
            const threshold = SLA_HOURS[p] || SLA_HOURS.standard;
            return hoursSince(c.status_updated_at || c.updated_at || c.created_at) > threshold;
        });
    }, [queueCases]);

    /* Quick stats. */
    const stats = useMemo(() => {
        const shippedToday = shippedCases.filter((c) => isToday(c.shipped_at)).length;
        const shipped7d = shippedCases.filter((c) => isInLast7Days(c.shipped_at)).length;
        const inFlight = queueCases.filter((c) => c.status === "in_production").length;

        // Avg cycle: approved → shipped, last 7 days
        const cycleHours = [];
        for (const c of shippedCases) {
            if (!isInLast7Days(c.shipped_at)) continue;
            const tl = c.timeline || [];
            const approved = tl.find((e) => e.action === "approved" || e.action === "case_approved" || e.action === "production_assigned");
            if (approved && c.shipped_at) {
                const h = (new Date(c.shipped_at).getTime() - new Date(approved.created_at).getTime()) / 3600000;
                if (h > 0 && h < 24 * 30) cycleHours.push(h);
            }
        }
        const avgCycle = cycleHours.length ? Math.round(cycleHours.reduce((a, b) => a + b, 0) / cycleHours.length) : null;

        return { shippedToday, shipped7d, inFlight, avgCycle };
    }, [queueCases, shippedCases]);

    /* Up Next — priority-first (STAT → Rush → Standard), then oldest. Show
       6 rows now so the section is a meaningful preview, not a teaser. */
    const upNext = useMemo(() => {
        const active = queueCases.filter((c) => ["approved", "assigned_to_production", "submitted_to_production", "in_production"].includes(c.status));
        const priorityRank = { stat: 0, rush: 1, standard: 2 };
        active.sort((a, b) => {
            const pa = priorityRank[a.production_priority || "standard"] ?? 2;
            const pb = priorityRank[b.production_priority || "standard"] ?? 2;
            if (pa !== pb) return pa - pb;
            const ta = new Date(a.status_updated_at || a.updated_at || a.created_at || 0).getTime();
            const tb = new Date(b.status_updated_at || b.updated_at || b.created_at || 0).getTime();
            return ta - tb;
        });
        return active.slice(0, 6);
    }, [queueCases]);

    const cleanFirst = (() => {
        const raw = (user?.name || "").trim();
        const noParen = raw.replace(/\s*\([^)]*\)\s*$/, "").trim();
        return (noParen.split(/\s+/)[0] || "") || "there";
    })();

    return (
        <div data-testid="production-dashboard" className="space-y-5 min-w-0">

            {/* 1 — HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="production-dash-greeting">
                        Hi, {cleanFirst}
                    </h1>
                    {/* iter97.19 — My Shift context chip. Helps the tech anchor
                        their day + see assigned-to-me load at a glance. */}
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                        {(() => {
                            const s = currentShift();
                            return (
                                <span
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${s.cls}`}
                                    data-testid="production-dash-shift-chip"
                                >
                                    <s.Icon size={12} weight="fill" />
                                    {s.label} · {fmtDateShort(new Date())}
                                </span>
                            );
                        })()}
                        <span className="text-xs text-muted-foreground">
                            {loading
                                ? "Loading the production floor…"
                                : totalAction === 0 && counts.in_production === 0
                                    ? "All clear — no cases waiting to be picked up."
                                    : `${totalAction} case${totalAction === 1 ? "" : "s"} ${totalAction === 1 ? "needs" : "need"} your attention today.`}
                        </span>
                    </div>
                </div>
                <Button
                    onClick={() => navigate("/coe/production/queue")}
                    variant="outline"
                    className="rounded-full h-11 px-5"
                    data-testid="production-dash-open-queue-btn"
                >
                    <Tray size={16} className="mr-1.5" weight="bold" /> Open queue
                </Button>
            </div>

            {/* 2 — Priority breach hero banner */}
            {slaBreaches.length > 0 && (
                <div
                    className="flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-2xl px-5 py-3"
                    data-testid="production-dash-sla-banner"
                >
                    <Fire size={20} weight="fill" className="text-rose-700 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-rose-900">
                            {slaBreaches.length} priority case{slaBreaches.length === 1 ? "" : "s"} sitting too long
                        </div>
                        <div className="text-xs text-rose-800/80 mt-0.5">
                            STAT &gt; {SLA_HOURS.stat}h · Rush &gt; {SLA_HOURS.rush}h · Standard &gt; {SLA_HOURS.standard}h
                        </div>
                    </div>
                    <Link
                        to="/coe/production/queue?priority=rush"
                        className="text-xs text-rose-900 font-semibold hover:underline whitespace-nowrap shrink-0"
                        data-testid="production-dash-sla-link"
                    >
                        Triage now <ArrowRight size={11} className="inline ml-0.5" />
                    </Link>
                </div>
            )}

            {/* 3 — Action tile grid (compact, 6-up priority view) */}
            <SectionCard
                title="By priority"
                subtitle={totalAction === 0 ? "No work in your queue right now." : `${totalAction} case${totalAction === 1 ? "" : "s"} waiting on the floor`}
                testid="production-action-section"
            >
                {loading ? (
                    <SkeletonTileRow />
                ) : totalAction === 0 && counts.in_production === 0 ? (
                    <EmptyAllClear />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="production-action-tiles">
                        {TILE_META.map((meta) => (
                            <CompactActionTile
                                key={meta.id}
                                meta={meta}
                                count={counts[meta.id] || 0}
                            />
                        ))}
                    </div>
                )}
            </SectionCard>

            {/* 3b — Stage-aware tile grid (4-up). Surfaces where the work is
                along the 10-stage SOP, not just the priority. Hidden if the
                whole queue is empty (no stages to surface). */}
            {!loading && (counts.in_production + counts.awaiting + counts.doctor_planned) > 0 && (
                <SectionCard
                    title="By production stage"
                    subtitle="Where the work is along the 10-stage SOP"
                    testid="production-stage-section"
                >
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="production-stage-tiles">
                        {STAGE_TILE_META.map((meta) => (
                            <CompactActionTile
                                key={meta.id}
                                meta={meta}
                                count={stageCounts[meta.id] || 0}
                            />
                        ))}
                    </div>
                </SectionCard>
            )}

            {/* 4 — Today's quick stats */}
            <SectionCard
                title="Today's quick stats"
                subtitle="Throughput, in-flight load, and average cycle time"
                testid="production-stats-section"
            >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <QuickStat
                        Icon={Truck}
                        tone="emerald"
                        value={loading ? "—" : stats.shippedToday}
                        label="Shipped today"
                        sub={`${stats.shipped7d} this week`}
                        testid="production-stat-shipped-today"
                    />
                    <QuickStat
                        Icon={Package}
                        tone="primary"
                        value={loading ? "—" : stats.inFlight}
                        label="In production"
                        sub="Currently on the floor"
                        testid="production-stat-in-production"
                    />
                    <QuickStat
                        Icon={ClockClockwise}
                        tone="violet"
                        value={loading ? "—" : (stats.avgCycle != null ? `${stats.avgCycle}h` : "—")}
                        label="Avg cycle"
                        sub="Approved → shipped"
                        /* iter97.19 — Benchmark indicator so the tech knows
                           if the current avg is healthy. Auto-coloured. */
                        benchmark={(() => {
                            if (loading || stats.avgCycle == null) {
                                return { label: `Target: ${TARGET_CYCLE_HOURS}h`, tone: "muted" };
                            }
                            if (stats.avgCycle <= TARGET_CYCLE_HOURS) {
                                return { label: `On target (≤${TARGET_CYCLE_HOURS}h)`, tone: "emerald" };
                            }
                            if (stats.avgCycle <= TARGET_CYCLE_HOURS * 2) {
                                return { label: `Above target (${TARGET_CYCLE_HOURS}h)`, tone: "amber" };
                            }
                            return { label: `Way above target (${TARGET_CYCLE_HOURS}h)`, tone: "rose" };
                        })()}
                        testid="production-stat-avg-cycle"
                    />
                    <QuickStat
                        Icon={ShieldWarning}
                        tone={slaBreaches.length > 0 ? "rose" : "neutral"}
                        value={loading ? "—" : `${slaBreaches.length}`}
                        label="At-risk"
                        sub={slaBreaches.length > 0 ? "Past priority SLA" : "Within SLA"}
                        testid="production-stat-at-risk"
                    />
                </div>
            </SectionCard>

            {/* 5 — Up Next */}
            <SectionCard
                title="Up next"
                subtitle="STAT → Rush → Standard, then oldest"
                testid="production-upnext-section"
                rightSlot={
                    <Link to="/coe/production/queue" className="text-xs text-primary font-semibold hover:underline whitespace-nowrap" data-testid="production-upnext-see-all">
                        See full queue <ArrowRight size={11} className="inline ml-0.5" />
                    </Link>
                }
            >
                {loading ? (
                    <div className="text-xs text-muted-foreground py-6 text-center">Loading…</div>
                ) : upNext.length === 0 ? (
                    <div className="bg-card border border-dashed border-border rounded-2xl p-8 text-center text-xs text-muted-foreground" data-testid="production-upnext-empty">
                        Nothing in the queue — newly-approved cases will appear here automatically.
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        {upNext.map((c) => <UpNextRow key={c.id} c={c} />)}
                    </div>
                )}
            </SectionCard>
        </div>
    );
}


/* ────────── Compact action tile ────────── */
function CompactActionTile({ meta, count }) {
    const t = TONES[meta.tone] || TONES.violet;
    const zero = count === 0;
    return (
        <Link
            to={meta.to}
            data-testid={`production-action-${meta.id}`}
            className={`group relative rounded-xl p-3 ${t.bg} ${t.text} ${zero ? "opacity-55" : ""} transition-all hover:-translate-y-0.5 hover:shadow-sm border ${t.border}`}
        >
            <div className="flex items-center gap-2.5">
                <div className={`w-7 h-7 rounded-lg ${t.soft} ${t.icon} flex items-center justify-center shrink-0`}>
                    <meta.Icon size={13} weight="duotone" />
                </div>
                <div className="font-display text-2xl font-bold tabular-nums leading-none flex-1">{count}</div>
                <ArrowRight size={11} className={`${t.sub} opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all`} />
            </div>
            <div className="flex items-center justify-between gap-1.5 mt-2 min-h-[16px]">
                <div className="text-[11px] font-semibold leading-tight truncate">{meta.label}</div>
            </div>
        </Link>
    );
}


/* ────────── Quick stat ────────── */
function QuickStat({ Icon, tone = "neutral", value, label, sub, testid, benchmark }) {
    const tones = {
        emerald: { border: "border-emerald-200", bg: "bg-emerald-50",  icon: "text-emerald-700", num: "text-emerald-900" },
        violet:  { border: "border-violet-200",  bg: "bg-violet-50",   icon: "text-violet-700",  num: "text-violet-900" },
        primary: { border: "border-primary/20",  bg: "bg-primary/5",   icon: "text-primary",     num: "text-foreground" },
        amber:   { border: "border-amber-200",   bg: "bg-amber-50",    icon: "text-amber-700",   num: "text-amber-900" },
        rose:    { border: "border-rose-200",    bg: "bg-rose-50",     icon: "text-rose-700",    num: "text-rose-900" },
        neutral: { border: "border-border",      bg: "bg-card",        icon: "text-muted-foreground", num: "text-foreground" },
    };
    const benchmarkTones = {
        emerald: "text-emerald-700 bg-emerald-100/60",
        amber:   "text-amber-700 bg-amber-100/60",
        rose:    "text-rose-700 bg-rose-100/60",
        muted:   "text-muted-foreground bg-secondary/60",
    };
    const c = tones[tone] || tones.neutral;
    return (
        <div className={`rounded-xl border ${c.border} ${c.bg} p-3 shadow-sm`} data-testid={testid}>
            <div className="flex items-center gap-1.5 mb-1.5">
                <Icon size={13} weight="fill" className={c.icon} />
                <div className="text-[9px] font-bold tracking-[0.16em] uppercase text-foreground/70">{label}</div>
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums leading-none ${c.num}`}>{value}</div>
            <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{sub}</div>
            {/* iter97.19 — Optional benchmark indicator (Target: 24h etc). */}
            {benchmark && (
                <div
                    className={`mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-wide ${benchmarkTones[benchmark.tone] || benchmarkTones.muted}`}
                    data-testid={`${testid}-benchmark`}
                >
                    <Target size={9} weight="fill" />
                    {benchmark.label}
                </div>
            )}
        </div>
    );
}


/* ────────── Up next row ────────── */
function UpNextRow({ c }) {
    const hrs = Math.round(hoursSince(c.status_updated_at || c.updated_at || c.created_at));
    const p = c.production_priority || "standard";
    const threshold = SLA_HOURS[p] || SLA_HOURS.standard;
    const ageTone = hrs > threshold ? "text-rose-700" : hrs > threshold * 0.6 ? "text-amber-700" : "text-muted-foreground";
    const priorityBadge =
        p === "stat" ? { label: "STAT", cls: "bg-rose-600 text-white" }
        : p === "rush" ? { label: "Rush", cls: "bg-amber-500 text-white" }
        : null;
    /* iter97.19 — Left-border accent so priority is scannable at the row level
       without reading the badge. */
    const borderAccent =
        p === "stat" ? "border-l-4 border-l-rose-600"
        : p === "rush" ? "border-l-4 border-l-amber-500"
        : "border-l-4 border-l-slate-300";
    const statusBadge = STATUS_PILL_FOR_UPNEXT[c.status] || STATUS_PILL_FOR_UPNEXT.default;

    /* Aligner count + material spec — pulled inline so the tech can scan
       the workload without opening the case. */
    const upper = c?.plan?.aligner_count_upper;
    const lower = c?.plan?.aligner_count_lower;
    const alignerStr = (upper != null || lower != null) ? `U${upper ?? 0} · L${lower ?? 0}` : null;
    const sheetName = c?.manufacturing_spec?.sheet_name;

    /* Due-date: derive from case.due_date if backend exposes one. We fall
       back to status_updated_at + standard SLA offset. */
    const dueDateIso = c?.due_date || (() => {
        const base = new Date(c.status_updated_at || c.updated_at || c.created_at || Date.now());
        return new Date(base.getTime() + threshold * 3600000).toISOString();
    })();
    const dueDate = new Date(dueDateIso);
    const now = new Date();
    const isOverdue = dueDate.getTime() < now.getTime();
    const isToday_ = isToday(dueDateIso);
    const dueLabel = isOverdue
        ? "Overdue"
        : isToday_
            ? "Today"
            : dueDate.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    const dueCls = isOverdue
        ? "bg-rose-600 text-white"
        : isToday_
            ? "bg-amber-100 text-amber-800 border border-amber-200"
            : "bg-slate-100 text-slate-700 border border-slate-200";

    return (
        <div
            className={`flex items-center gap-3 bg-card border border-border ${borderAccent} rounded-xl p-3 hover:shadow-sm transition-all`}
            data-testid={`production-upnext-row-${c.id}`}
        >
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Package size={18} weight="duotone" className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-sm truncate">{c.patient_name || "Patient"}</span>
                    {c.code && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{c.code}</span>}
                    {priorityBadge && (
                        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${priorityBadge.cls}`}>
                            {priorityBadge.label}
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    Dr. {(c.doctor_name || "").replace(/^Dr\.\s*/i, "")} · {c.diagnosis || "—"}
                </div>
                {/* iter97.19 — Aligner + material spec inline so the row carries
                    a meaningful preview of the job. */}
                {(alignerStr || sheetName) && (
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap text-[10px]">
                        {alignerStr && (
                            <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary text-foreground/80 font-semibold tabular-nums"
                                title="Upper / Lower aligner count"
                            >
                                <Tooth size={9} weight="fill" />
                                {alignerStr}
                            </span>
                        )}
                        {sheetName && (
                            <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-800 border border-cyan-200 font-semibold truncate max-w-[200px]"
                                title={sheetName}
                            >
                                <Stack size={9} weight="fill" />
                                {sheetName}
                            </span>
                        )}
                    </div>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {/* Due-date chip — today/overdue colour-coded */}
                <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${dueCls}`}
                    title={`Due: ${dueDate.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`}
                    data-testid={`production-upnext-due-${c.id}`}
                >
                    {dueLabel}
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${statusBadge.cls}`}>
                    {statusBadge.label}
                </span>
                <div className={`text-[11px] font-semibold tabular-nums ${ageTone}`}>{hrs}h</div>
                {/* Direct Open button per the spec — primary intent of the row */}
                <Link
                    to={`/coe/production/case/${c.id}`}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-primary text-white text-[10px] font-bold uppercase tracking-wide hover:bg-primary/90 transition-colors shrink-0"
                    data-testid={`production-upnext-open-${c.id}`}
                >
                    Open <ArrowRight size={9} weight="bold" />
                </Link>
            </div>
        </div>
    );
}

const STATUS_PILL_FOR_UPNEXT = {
    approved: { label: "Awaiting", cls: "bg-emerald-100 text-emerald-700" },
    assigned_to_production: { label: "Assigned", cls: "bg-emerald-100 text-emerald-700" },
    submitted_to_production: { label: "Doctor-planned", cls: "bg-cyan-100 text-cyan-700" },
    in_production: { label: "In production", cls: "bg-blue-100 text-blue-700" },
    default: { label: "Queued", cls: "bg-slate-100 text-slate-700" },
};


/* ────────── Reusable bits ────────── */

/* iter97.19 — Section card. Wraps each dashboard block in a subtle
   light-grey container with a thin border so the page has visual
   structure instead of floating tiles. */
function SectionCard({ title, subtitle, rightSlot, testid, children }) {
    return (
        <section
            className="rounded-2xl bg-[#F7F8FA] border border-[#E5E7EB]/80 p-4 sm:p-5"
            data-testid={testid}
        >
            <div className="flex items-end justify-between mb-3 px-1">
                <div>
                    <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
                    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
                </div>
                {rightSlot}
            </div>
            {children}
        </section>
    );
}


function SectionHeader({ title, subtitle, rightSlot }) {
    return (
        <div className="flex items-end justify-between mb-3 px-1">
            <div>
                <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
                {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
            {rightSlot}
        </div>
    );
}


function EmptyAllClear() {
    return (
        <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-4" data-testid="production-action-empty">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <CheckCircle size={22} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-foreground">All clear</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                    No cases waiting for production. New approved cases will land here automatically.
                </div>
            </div>
        </div>
    );
}


function SkeletonTileRow() {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-20 bg-card border border-border rounded-xl animate-pulse" />
            ))}
        </div>
    );
}
